<template>
  <ui-dialog :show="isOpen" max-width="623" @input="handleClose">
    <template #header>
      <h3>
        {{ $t("message.posts.facilitiesToAddDialog.title") }}
      </h3>
    </template>

    <template #default>
      <v-text-field
        v-if="loaded && facilities && facilities.length"
        v-model="facilitiesSearchInputValue"
        :placeholder="
          $t(
            'message.businessAddresses.groups.facilities.filterInputPlaceholder'
          )
        "
        prepend-inner-icon="icon-search"
        clearable
        outlined
        rounded
        class="mb-4"
        :hide-details="true"
        @input="onFacilitiesSearch"
      ></v-text-field>
      <v-row no-gutters class="align-center mb-3">
        <v-col>
          <Pagination
            class="flex-grow-1"
            :page="page"
            :per-page="perPage"
            :options="perPageOptions"
            :total-count="totalCount"
            :disabled="loading"
            @change="handleChange"
          >
            <div class="post-facilities-dialog__positions-count">
              {{ $t("message.common.positionsCount", [totalCount]) }}
            </div>
          </Pagination>
        </v-col>
      </v-row>

      <div
        v-if="loaded && facilities && facilities.length"
        class="position-relative"
      >
        <v-virtual-scroll
          class="list"
          :items="facilities"
          :item-height="listItemHeight"
          :height="listHeight"
          ref="list"
        >
          <template v-slot:default="{ item }">
            <v-list-item :key="item.id" :ripple="false" class="list-item">
              <v-list-item-content class="d-flex flex-col">
                <div class="d-flex align-center">
                  <IsOwnerIndicator :isOwner="true" class="mr-3" />
                  <div class="list-item__content-primary">
                    <p class="list-item__name">{{ item.name }}</p>
                    <div class="list-item__description">
                      <p class="list-item__address">
                        {{ item.formatted_address }}
                      </p>
                    </div>
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-virtual-scroll>

        <SectionLoader :status="loading" :opacity="0.75" />
      </div>
      <div v-else class="no-data">
        <p class="mb-0">
          {{ $t("message.posts.facilitiesToAddDialog.noFacilities") }}
        </p>
        <SectionLoader :status="loading" :opacity="0.75" />
      </div>
    </template>

    <template #actions>
      <v-row no-gutters class="bottom-pagination">
        <v-col>
          <Pagination
            class="flex-grow-1"
            :page="page"
            :per-page="perPage"
            :show-options="false"
            :options="perPageOptions"
            :total-count="totalCount"
            :disabled="loading"
            @change="handleChange"
          />
        </v-col>
      </v-row>
    </template>
  </ui-dialog>
</template>

<script>
import Pagination from "@/components/List/Pagination";
import SectionLoader from "@/components/Loader/SectionLoader";
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";

import { debounce, scrollToTop } from "@/helpers/helpers";
import serviceHandler from "@/helpers/handlers/service";
import facilitiesService from "@/services/facilities";

export default {
  name: "FacilitiesToAddPostDialog",
  components: { Pagination, SectionLoader, IsOwnerIndicator },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    facilitiesIds: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      loaded: false,
      loading: false,
      facilitiesSearchInputValue: "",
      totalCount: 0,
      page: 1,
      perPageOptions: [10, 25, 50, 100],
      perPage: 10,
      facilities: [],
      listItemHeight: 65
    };
  },
  watch: {
    async isOpen(newVal) {
      if (newVal && this.facilitiesIds && this.facilitiesIds.length) {
        this.loading = true;
        await this.fetchFacilities();
        this.loading = false;
        this.loaded = true;
      } else {
        this.loaded = false;
        this.facilitiesSearchInputValue = "";
        this.page = 1;
        this.perPage = 10;
        this.totalCount = 0;
        this.facilities = [];
      }
    }
  },
  computed: {
    listHeight() {
      const maxListItems = 7;

      if (this.facilities.length > maxListItems) {
        return this.listItemHeight * maxListItems;
      } else if (this.facilities.length < 2) {
        return this.listItemHeight * 2;
      } else {
        return this.listItemHeight * this.facilities.length + 1; // 1px added to avoid scrollbar show up
      }
    }
  },
  methods: {
    async fetchFacilities() {
      const params = {
        page: this.page,
        perPage: this.perPage,
        query: this.facilitiesSearchInputValue,
        is_owner: true,
        is_verified: true,
        facilities_ids: this.facilitiesIds
      };

      const response = await serviceHandler(
        facilitiesService.getFacilitiesData(params)
      );

      if (response && "results" in response && "count" in response) {
        this.facilities = [...response.results];
        this.totalCount = response.count;
      }
    },

    onFacilitiesSearch: debounce(async function() {
      this.loading = true;
      this.page = 1;
      await this.fetchFacilities();
      this.loading = false;
    }, 500),

    async handleChange({ page, perPage }) {
      this.$nextTick(() => {
        scrollToTop(this.$refs.list.$el);
      });
      this.loading = true;
      this.page = page;
      this.perPage = perPage;
      await this.fetchFacilities();
      this.loading = false;
    },

    handleClose() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.list {
  border-top: 1px solid $light-3;
  position: relative;
}

.list-item {
  border-bottom: 1px solid $light-3;

  &__name,
  &__description {
    color: $dark;
  }

  &__name {
    @include font-size(14px);
    color: $text;
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
  }

  &__description {
    position: relative;
    display: flex;
    @include font-size(12px);
    font-weight: 400;
    margin-bottom: 0;
    max-width: 100%;
    white-space: nowrap;
    color: $text;

    & > p {
      margin-bottom: 0;
    }
  }
}

.contact-link {
  margin-top: $spacing * 0.5;
  color: $pink;
  font-weight: 500;
}

.bottom-pagination {
  margin-bottom: $spacing * 1.5;
  position: relative;
}

.no-data {
  @include noData(100px);
}
</style>

<style lang="scss">
.post-facilities-dialog__positions-count {
  color: $text;
  font-weight: 400;
  @include font-size(12px);
}
</style>
